import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Instagram, Send, ExternalLink } from "lucide-react";

const TEMPLATES = [
  { k: "os_pronto", label: "OS Pronta", text: "Olá {nome}! Sua ordem de serviço está pronta para retirada na UPCEL. Aguardamos você!" },
  { k: "orcamento", label: "Orçamento", text: "Olá {nome}! Segue o orçamento do seu reparo: R$ {valor}. Podemos prosseguir?" },
  { k: "promocao", label: "Promoção", text: "Olá {nome}! Temos novidades e promoções especiais para você na UPCEL. Confira!" },
  { k: "pos_venda", label: "Pós-venda", text: "Olá {nome}! Tudo bem com seu produto? Qualquer dúvida, estamos à disposição. — UPCEL" },
];

export default function Messages() {
  const [history, setHistory] = useState([]);
  const [channel, setChannel] = useState("whatsapp");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [content, setContent] = useState("");
  const [template, setTemplate] = useState("");
  const [customers, setCustomers] = useState([]);

  const load = () => {
    api.get("/messages").then((r) => setHistory(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  };
  useEffect(() => { load(); }, []);

  const applyTemplate = (k) => {
    const t = TEMPLATES.find((x) => x.k === k);
    if (!t) return;
    setContent(t.text.replace("{nome}", name || "cliente").replace("{valor}", "___"));
    setTemplate(k);
  };

  const openAndLog = async () => {
    if (!contact || !content) { toast.error("Preencha contato e mensagem"); return; }
    const url = channel === "whatsapp"
      ? `https://wa.me/${contact.replace(/\D/g, "")}?text=${encodeURIComponent(content)}`
      : `https://instagram.com/${contact.replace("@", "")}`;
    window.open(url, "_blank");
    try {
      await api.post("/messages", { channel, recipient_name: name, recipient_contact: contact, content, template });
      toast.success("Mensagem registrada no histórico");
      setContent(""); load();
    } catch { toast.error("Erro ao registrar"); }
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Comunicação</div>
        <h1 className="font-display text-4xl font-bold">WhatsApp & Instagram</h1>
        <p className="text-slate-500 text-sm mt-1">Envie mensagens rápidas com templates e registre o histórico de atendimento.</p>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-5 space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setChannel("whatsapp")}
              data-testid="msg-whatsapp-tab"
              className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold ${channel === "whatsapp" ? "bg-emerald-500 text-white" : "bg-slate-100"}`}>
              <MessageSquare size={14} /> WhatsApp
            </button>
            <button onClick={() => setChannel("instagram")}
              data-testid="msg-instagram-tab"
              className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold ${channel === "instagram" ? "bg-pink-500 text-white" : "bg-slate-100"}`}>
              <Instagram size={14} /> Instagram
            </button>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Cliente (rápido)</label>
            <select onChange={(e) => {
              const c = customers.find((x) => x.id === e.target.value);
              if (c) { setName(c.name); setContact(channel === "whatsapp" ? c.phone : ""); }
            }} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
              <option value="">Selecionar cliente cadastrado...</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.phone}</option>)}
            </select>
          </div>

          <input type="text" placeholder="Nome do destinatário" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="msg-name-input"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <input type="text" placeholder={channel === "whatsapp" ? "Telefone (ex: 5511999999999)" : "@usuario Instagram"}
            value={contact} onChange={(e) => setContact(e.target.value)}
            data-testid="msg-contact-input"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Templates</label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.k} onClick={() => applyTemplate(t.k)}
                  data-testid={`msg-template-${t.k}`}
                  className="text-xs border border-slate-300 hover:border-[#2563EB] hover:text-[#2563EB] px-2 py-1 rounded-sm">
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <textarea rows={6} placeholder="Mensagem..." value={content} onChange={(e) => setContent(e.target.value)}
            data-testid="msg-content-input"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />

          <button onClick={openAndLog}
            data-testid="msg-send-btn"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2.5 rounded-sm font-semibold text-sm flex items-center gap-2">
            <Send size={14} /> Abrir {channel === "whatsapp" ? "WhatsApp" : "Instagram"} e registrar
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-display font-bold text-lg mb-3">Histórico</h3>
          <ul className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin">
            {history.map((m) => (
              <li key={m.id} className="border border-slate-100 p-3 rounded-sm text-sm" data-testid={`msg-history-${m.id}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">{m.channel}</span>
                  <span className="text-xs text-slate-400">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="font-medium">{m.recipient_name} <span className="text-slate-400">• {m.recipient_contact}</span></div>
                <div className="text-slate-600 text-xs mt-1 line-clamp-3">{m.content}</div>
              </li>
            ))}
            {history.length === 0 && <div className="text-sm text-slate-500">Sem histórico ainda.</div>}
          </ul>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm text-sm text-blue-900">
        <strong>Integração oficial Meta API:</strong> Ao obter credenciais do Meta Business (Phone Number ID, Access Token, Instagram Business ID), cadastre em <em>Configurações</em> para ativar envio automatizado via Cloud API.
        <ExternalLink size={12} className="inline ml-1" />
      </div>
    </div>
  );
}
